<template>
    <v-card outlined class="mb-3">
        <v-card-title class="text-subtitle-1 d-flex align-center">
            <v-icon left small>mdi-source-branch</v-icon>
            Trilha da Turma
            <v-spacer />
            <v-btn
                small
                color="primary"
                :disabled="!groupId"
                @click="openDialog"
            >
                <v-icon left small>mdi-plus</v-icon>
                Vincular turma
            </v-btn>
        </v-card-title>
        <v-card-subtitle class="pb-2">
            Continuidade desta turma em outros períodos letivos. Vincule turmas equivalentes
            para preservar o histórico de disciplinas cursadas.
        </v-card-subtitle>

        <v-card-text>
            <v-alert v-if="!groupId" type="info" text dense>
                Salve a turma antes de gerenciar a trilha.
            </v-alert>

            <template v-else>
                <v-data-table
                    :headers="headers"
                    :items="state.linked_groups || []"
                    :loading="loading"
                    hide-default-footer
                    :items-per-page="50"
                    dense
                >
                    <template v-slot:item.work_time="{ item }">
                        {{ item.work_time ? item.work_time.name : '—' }}
                    </template>

                    <template v-slot:item.name="{ item }">
                        <span :class="{ 'font-weight-bold': item.is_current }">
                            {{ item.name }}
                        </span>
                        <v-chip v-if="item.is_current" x-small class="ml-2" color="primary">
                            atual
                        </v-chip>
                    </template>

                    <template v-slot:item.curriculum="{ item }">
                        <v-tooltip v-if="item.curriculum" bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <span v-bind="attrs" v-on="on" class="curriculum-cell">
                                    {{ item.curriculum.code || item.curriculum.name }}
                                </span>
                            </template>
                            <span>{{ item.curriculum.name }}</span>
                        </v-tooltip>
                        <span v-else class="grey--text">—</span>
                    </template>

                    <template v-slot:item.actions="{ item }">
                        <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    x-small
                                    text
                                    color="red"
                                    :loading="busyId === item.id"
                                    @click="confirmUnlink(item)"
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-icon x-small left>mdi-link-variant-off</v-icon>
                                    Remover da trilha
                                </v-btn>
                            </template>
                            <span>
                                Remove apenas esta turma da trilha.
                                As demais permanecem vinculadas.
                            </span>
                        </v-tooltip>
                    </template>
                </v-data-table>

                <div v-if="suggestionItems.length || suggestionMeta.total > 0" class="mt-4">
                    <div class="text-subtitle-2 mb-2 d-flex align-center">
                        <v-icon small left>mdi-lightbulb-outline</v-icon>
                        Sugestões automáticas ({{ suggestionMeta.total }})
                        <v-spacer />
                        <v-select
                            v-model="suggestionsPerPage"
                            :items="[5, 10, 20, 50]"
                            dense
                            outlined
                            hide-details
                            label="Por página"
                            style="max-width: 130px"
                            @change="onPerPageChange"
                        />
                    </div>
                    <v-list dense outlined rounded class="suggestions-list">
                        <template v-for="(s, idx) in suggestionItems">
                            <v-divider v-if="idx > 0" :key="'div-' + s.id" />
                            <v-list-item
                                :key="s.id"
                                class="suggestion-row"
                                @click="linkOne(s.id)"
                            >
                                <v-list-item-content>
                                    <v-list-item-title>
                                        {{ s.work_time ? s.work_time.name : '?' }} — {{ s.name }}
                                        <small class="grey--text">({{ s.code }})</small>
                                    </v-list-item-title>
                                    <v-list-item-subtitle>
                                        {{ s.students || 0 }} alunos
                                        <span v-if="s.curriculum">
                                            · matriz <strong>{{ s.curriculum.code || s.curriculum.name }}</strong>
                                        </span>
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                    <v-btn
                                        x-small
                                        outlined
                                        color="primary"
                                        :loading="busyId === s.id"
                                        @click.stop="linkOne(s.id)"
                                    >
                                        <v-icon x-small left>mdi-link-variant-plus</v-icon>
                                        Vincular
                                    </v-btn>
                                </v-list-item-action>
                            </v-list-item>
                        </template>
                    </v-list>
                    <div
                        v-if="suggestionMeta.last_page > 1"
                        class="d-flex justify-center mt-3"
                    >
                        <v-pagination
                            v-model="suggestionsPage"
                            :length="suggestionMeta.last_page"
                            :total-visible="7"
                            @input="onPageChange"
                        />
                    </div>
                    <v-alert
                        dense
                        text
                        border="left"
                        color="info"
                        icon="mdi-information-outline"
                        class="mt-3 suggestions-footnote"
                    >
                        <div class="text-caption">
                            <strong class="d-block mb-1">Como geramos estas sugestões</strong>
                            Turmas com <strong>mesmo curso, campus e turno</strong> da turma atual,
                            em <strong>períodos letivos anteriores</strong>, que ainda não fazem parte de outra trilha.
                            A lista é ordenada por <strong>período letivo</strong> (mais recente primeiro),
                            com desempate por nome da turma.
                        </div>
                    </v-alert>
                </div>
            </template>

            <trail-link-dialog
                v-if="groupId"
                :show.sync="dialog"
                :group-id="groupId"
                :course-id="courseId"
                :current-work-time-id="currentWorkTimeId"
                :excluded-ids="excludedIds"
                :excluded-names="excludedNames"
                @link="onLinkFromDialog"
            />

            <emc-alert-modal
                text="Remover esta turma da trilha? Apenas ela será desvinculada — as demais permanecem juntas."
                v-bind:show.sync="confirmDialog"
            >
                <template v-slot:actions>
                    <v-btn color="error" @click.prevent.stop="doUnlink()">
                        Remover da trilha <v-icon right dark>mdi-link-variant-off</v-icon>
                    </v-btn>
                </template>
            </emc-alert-modal>
        </v-card-text>
    </v-card>
</template>

<style scoped>
.curriculum-cell {
    cursor: help;
    border-bottom: 1px dotted rgba(0, 0, 0, 0.3);
}
.suggestions-footnote {
    line-height: 1.5;
}
.suggestions-footnote .v-chip {
    margin: 0 2px;
}

.suggestions-list {
    padding: 0;
}
.suggestion-row {
    cursor: pointer;
    transition: background-color 120ms ease, border-left-color 120ms ease;
    border-left: 3px solid transparent;
}
.suggestion-row:hover {
    background-color: #EFF6FF; /* azul muito claro */
    border-left-color: var(--v-primary-base, #1976D2);
}
.suggestion-row:hover .v-btn {
    background-color: var(--v-primary-base, #1976D2);
    color: white !important;
}
.suggestion-row:hover .v-list-item__title {
    color: var(--v-primary-base, #1976D2);
}
</style>

<script>
import { computed, getCurrentInstance, reactive, ref, watch } from 'vue'
import TrailLinkDialog from './TrailLinkDialog.vue'

export default {
    name: 'GroupTrail',
    components: { TrailLinkDialog },
    props: {
        groupId:  { type: [Number, String], default: null },
        courseId: { type: [Number, String], default: null },
    },
    setup(props) {
        const { proxy } = getCurrentInstance()
        const state = reactive({
            trail: null,
            linked_groups: [],
            suggestions: { data: [], meta: { current_page: 1, per_page: 10, total: 0, last_page: 1 } },
        })
        const loading = ref(false)
        const busyId  = ref(null)
        const dialog  = ref(false)
        const confirmDialog = ref(false)
        const pendingUnlink = ref(null)
        const suggestionsPage    = ref(1)
        const suggestionsPerPage = ref(10)

        const suggestionItems = computed(() => {
            const s = state.suggestions
            if (!s) return []
            return Array.isArray(s) ? s : (s.data || [])
        })
        const suggestionMeta = computed(() => {
            const s = state.suggestions
            if (s && s.meta) return s.meta
            return { current_page: 1, per_page: suggestionsPerPage.value, total: suggestionItems.value.length, last_page: 1 }
        })

        const headers = [
            { text: 'Período', value: 'work_time',  sortable: false },
            { text: 'Turma',   value: 'name',       sortable: false },
            { text: 'Código',  value: 'code',       sortable: false },
            { text: 'Matriz',  value: 'curriculum', sortable: false },
            { text: 'Alunos',  value: 'students',   sortable: false, width: 80 },
            { text: 'Ações',   value: 'actions',    sortable: false, width: 140 },
        ]

        const excludedIds = computed(() => (state.linked_groups || []).map((g) => g.id))

        // Período letivo da turma atual — usado pelo dialog pra esconder turmas paralelas.
        const currentWorkTimeId = computed(() => {
            const current = (state.linked_groups || []).find((g) => g.is_current)
            return current && current.work_time ? current.work_time.id : null
        })

        const excludedNames = computed(() => {
            const norm = (s) => (s || '').toString().trim().toLowerCase()
            return Array.from(new Set(
                (state.linked_groups || [])
                    .map((g) => norm(g.name))
                    .filter(Boolean)
            ))
        })

        const fetchTrail = async () => {
            if (!props.groupId) return
            loading.value = true
            try {
                const res = await proxy.$store.dispatch('domainGroup/ActionGetGroupTrail', {
                    id: props.groupId,
                    page:     suggestionsPage.value,
                    per_page: suggestionsPerPage.value,
                })
                Object.assign(state, {
                    trail:         res && res.trail         || null,
                    linked_groups: res && res.linked_groups || [],
                    suggestions:   res && res.suggestions   || { data: [], meta: { current_page: 1, per_page: suggestionsPerPage.value, total: 0, last_page: 1 } },
                })
                // Sincroniza a página atual com o que o backend devolveu
                if (state.suggestions && state.suggestions.meta) {
                    suggestionsPage.value = state.suggestions.meta.current_page
                }
            } finally {
                loading.value = false
            }
        }

        const onPageChange = (page) => {
            suggestionsPage.value = page
            fetchTrail()
        }

        const onPerPageChange = () => {
            suggestionsPage.value = 1
            fetchTrail()
        }

        const linkOne = async (otherGroupId) => {
            busyId.value = otherGroupId
            try {
                const res = await proxy.$store.dispatch('domainGroup/ActionLinkGroupTrail', {
                    id: props.groupId,
                    group_ids: [otherGroupId],
                })
                Object.assign(state, res)
            } finally {
                busyId.value = null
            }
        }

        const openDialog = () => { dialog.value = true }

        const onLinkFromDialog = async (groupIds) => {
            const res = await proxy.$store.dispatch('domainGroup/ActionLinkGroupTrail', {
                id: props.groupId,
                group_ids: groupIds,
            })
            Object.assign(state, res)
            dialog.value = false
        }

        const confirmUnlink = (item) => {
            pendingUnlink.value = item
            confirmDialog.value = true
        }

        const doUnlink = async () => {
            const item = pendingUnlink.value
            if (!item) return
            busyId.value = item.id
            confirmDialog.value = false
            try {
                const res = await proxy.$store.dispatch('domainGroup/ActionUnlinkGroupTrail', {
                    id: props.groupId,
                    otherGroupId: item.id,
                })
                Object.assign(state, res)
            } finally {
                busyId.value = null
                pendingUnlink.value = null
            }
        }

        watch(() => props.groupId, () => fetchTrail(), { immediate: true })

        return {
            state,
            loading,
            busyId,
            dialog,
            confirmDialog,
            headers,
            excludedIds,
            currentWorkTimeId,
            excludedNames,
            openDialog,
            linkOne,
            onLinkFromDialog,
            confirmUnlink,
            doUnlink,
            suggestionItems,
            suggestionMeta,
            suggestionsPage,
            suggestionsPerPage,
            onPageChange,
            onPerPageChange,
        }
    },
}
</script>
